// src/components/formBuilder/FormPreview.jsx
import React from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Switch,
  Slider,
} from "@mui/material";
import TextInputComponent from "../ui/TextInputComponent";
import SelectInputComponent from "../ui/SelectInputComponent";

/**
 * Read-only form preview that mirrors the layout used by the builder canvas.
 * - Two-column grid with support for field.layout.colSpan (1 | 2)
 * - Sections render as full-width blocks with title/subtitle
 * - All inputs are disabled (no validation, no state)
 */
export default function FormPreview({ schema }) {
  const fields = schema?.fields || [];

  const renderReadonlyField = (f) => {
    const colSpan = f?.layout?.colSpan === 2 ? 2 : 1;
    const common = {
      key: f.id,
      label: f.label || f.name || f.id,
      name: f.id,
      value: "", // preview only; no actual values here
      disabled: true,
      required: !!f.required,
      helperText: f.helperText || "",
    };

    if (f.type === "section") {
      return (
        <Paper
          key={f.id}
          variant="outlined"
          sx={{ p: 2, gridColumn: "1 / -1", bgcolor: "grey.50" }}
        >
          <Typography variant="subtitle1">{f.title || "Section"}</Typography>
          {f.subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {f.subtitle}
            </Typography>
          )}
          <Divider />
        </Paper>
      );
    }

    switch (f.type) {
      case "divider":
        return (
          <Divider key={f.id} sx={{ my: 2, gridColumn: `span ${colSpan}` }}>
            <Typography variant="caption" sx={{ px: 1 }}>
              {f.label || "Divider"}
            </Typography>
          </Divider>
        );

      case "textarea":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <TextInputComponent {...common} multiline />
          </Box>
        );

      case "number":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <TextInputComponent {...common} type="number" />
          </Box>
        );

      case "email":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <TextInputComponent {...common} type="email" />
          </Box>
        );

      case "date":
        // Preview-only: render as a plain disabled text input so no browser date default appears
        return (
            <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <TextInputComponent
                {...common}
                type="text"              // <-- use text, not date
                value=""                 // <-- force empty
                placeholder="YYYY-MM-DD" // optional hint
                disabled                 // already in {...common}, but explicit is fine
            />
            </Box>
        );

      case "select":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <SelectInputComponent
              label={common.label}
              value=""
              options={f.options || []}
              multiple={false}
              required={common.required}
              error={false}
              disabled
            />
          </Box>
        );

      case "multiselect":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <SelectInputComponent
              label={common.label}
              value={[]}
              options={f.options || []}
              multiple
              required={common.required}
              error={false}
              disabled
            />
          </Box>
        );

      case "radio":
        return (
          <Paper
            key={f.id}
            variant="outlined"
            sx={{ p: 1.5, gridColumn: `span ${colSpan}` }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {common.label} {common.required ? "*" : ""}
            </Typography>
            <RadioGroup row={!!f.inline} value="" onChange={() => {}}>
              {(f.options || []).map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio disabled />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
            {f.helperText && (
              <Typography variant="caption" color="text.secondary">
                {f.helperText}
              </Typography>
            )}
          </Paper>
        );

      case "checkbox":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <FormControlLabel
              control={<Checkbox checked={false} disabled />}
              label={common.label}
            />
            {f.helperText && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: -1 }}
              >
                {f.helperText}
              </Typography>
            )}
          </Box>
        );

      case "switch":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <FormControlLabel
              label={common.label}
              control={<Switch checked={false} disabled />}
            />
            {f.helperText && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: -1 }}
              >
                {f.helperText}
              </Typography>
            )}
          </Box>
        );

      case "slider":
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <Typography gutterBottom>
              {common.label} {common.required ? "*" : ""}
            </Typography>
            <Slider
              value={f.min ?? 0}
              min={f.min ?? 0}
              max={f.max ?? 100}
              step={f.step ?? 1}
              valueLabelDisplay="auto"
              disabled
            />
            {f.helperText && (
              <Typography variant="caption" color="text.secondary">
                {f.helperText}
              </Typography>
            )}
          </Box>
        );

      default:
        // text by default
        return (
          <Box key={f.id} sx={{ gridColumn: `span ${colSpan}` }}>
            <TextInputComponent {...common} />
          </Box>
        );
    }
  };

  // Group into sections (mirrors your form filler behavior)
  const sections = React.useMemo(() => {
    const out = [];
    let current = {
      title: schema?.title || "Form Preview",
      description: schema?.description || "This is a read-only preview.",
      items: [],
    };
    out.push(current);

    for (const f of fields) {
      if (f.type === "section") {
        current = {
          title: f.title || "Section",
          description: f.subtitle || "",
          items: [],
        };
        out.push(current);
      } else {
        current.items.push(f);
      }
    }
    return out;
  }, [schema, fields]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {schema?.title || "Untitled Form"}
      </Typography>
      {schema?.description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {schema.description}
        </Typography>
      ) : null}

      {sections.map((sec, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            {sec.title}
          </Typography>
          {sec.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {sec.description}
            </Typography>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              alignItems: "start",
            }}
          >
            {sec.items.map(renderReadonlyField)}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}